import React, { useState, useMemo } from 'react';
import { Box, Typography, Grid, IconButton, TextField, InputAdornment } from '@mui/material';
import { ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMarkets } from '../hooks/useMarketData';
import { usePageTitle } from '../hooks/usePageTitle';
import { BrowseMarketCard } from '../components/cards/BrowseMarketCard';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';

export const Markets: React.FC = () => {
  const navigate = useNavigate();
  const { countryId } = useParams<{ countryId: string }>();
  const { data, isLoading, error } = useMarkets(countryId ? parseInt(countryId) : null);
  const [searchQuery, setSearchQuery] = useState('');

  const markets = data?.markets || [];
  const continentId = data?.continentId;

  usePageTitle(data?.countryName ? `${data.countryName} Markets` : 'Markets');

  // Filter markets based on search query
  const filteredMarkets = useMemo(() => {
    if (!searchQuery.trim()) {
      return markets;
    }
    const query = searchQuery.toLowerCase();
    return markets.filter((market) =>
      market.name.toLowerCase().includes(query)
    );
  }, [markets, searchQuery]);

  if (isLoading) {
    return <LoadingSpinner message="Loading markets..." />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load markets" />;
  }

  return (
    <Box pb={10}>
      <Box px={2} pt={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => continentId && navigate(`/countries/${continentId}`)}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Markets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredMarkets.length} of {markets.length} markets
            </Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search markets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      <Box px={2}>
        {filteredMarkets.length === 0 && searchQuery ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="30vh"
          >
            <Typography variant="h6" gutterBottom>
              No Markets Found
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No markets match "{searchQuery}"
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredMarkets.map((market) => (
              <Grid item xs={12} key={market.id}>
                <BrowseMarketCard market={market} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};
